import { createFileRoute, useNavigate, Link, Outlet, useLocation } from '@tanstack/react-router';
import { useMaturityAssessments, useMaturityFrameworks, useService } from '../../graphql/queries';
import {
  DataTable,
  EmptyState,
  TableSkeleton,
  type DataTableColumn,
} from '../../components/shared';
import type { MaturityAssessment, MaturityFramework } from '../../graphql/types';
import { MaturitySubjectEntityType, AssessmentCadence } from '../../graphql/types';
import { useAppSelector } from '../../store';
import { selectUser } from '../../store/features/auth/authSlice';
import { canCreateAssessment } from '../../lib/permissions';
import { computeAssessmentScheduleInfo, cadenceLabel } from '../../lib/assessmentSchedule';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';
import './$serviceId.assessments.css';

export const Route = createFileRoute('/services/$serviceId/assessments')({
  component: ServiceAssessmentsPage,
});

export { ServiceAssessmentsPage };

/**
 * A service can be assessed under 2+ frameworks (e.g. Tyler Cloud Maturity
 * Model + ISO 25010, both seeded by default) — without a Framework column,
 * rows are visually indistinguishable and a 1-6 score reads as one scale
 * across frameworks that don't share one (#1603). The list query only
 * returns frameworkId/frameworkVersion, so names are resolved client-side
 * from the (small, non-paginated) frameworks list, keyed by entityId.
 */
function buildColumns(
  frameworksById: Map<string, MaturityFramework>
): DataTableColumn<MaturityAssessment>[] {
  return [
    {
      key: 'assessmentPeriod',
      label: 'Period',
      sortable: true,
      accessor: (a) => a.assessmentPeriod,
    },
    {
      key: 'framework',
      label: 'Framework',
      render: (a) => <span>{frameworksById.get(a.frameworkId)?.name ?? a.frameworkId}</span>,
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (a) => (
        <span
          className={`assessment-status-badge assessment-status-badge--${a.status.toLowerCase().replace('inprogress', 'in-progress')}`}
        >
          {a.overdue === true && (
            <span className="assessment-overdue-badge" aria-label="overdue">
              ⚠
            </span>
          )}
          {a.status}
        </span>
      ),
    },
    {
      key: 'overallScore',
      label: 'Overall Score',
      render: (a) =>
        a.overallScore != null ? (
          <span className="assessment-score">{a.overallScore.toFixed(1)} / 6</span>
        ) : (
          <span className="assessment-score--empty">—</span>
        ),
    },
    {
      key: 'completionPercentage',
      label: 'Completion',
      render: (a) => <span>{a.completionPercentage}%</span>,
    },
    {
      key: 'dueDate',
      label: 'Due Date',
      render: (a) => new Date(a.dueDate).toLocaleDateString(),
    },
  ];
}

function ServiceAssessmentsPage(): JSX.Element {
  const ForgeButton = 'forge-button' as React.ElementType;
  const { serviceId } = Route.useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useAppSelector(selectUser);

  const { data, loading, error } = useMaturityAssessments(
    { subjectEntityType: MaturitySubjectEntityType.Service, subjectEntityId: serviceId },
    20
  );

  const { data: serviceData } = useService(serviceId);

  // Small, non-paginated set (#1603) — used only to resolve the Framework
  // column's frameworkId -> name, not to gate loading/error handling below.
  const { data: frameworksData } = useMaturityFrameworks(50);

  // Render child routes (new, detail) independently. Checked after all hooks
  // above run unconditionally on every render — an early return before a hook
  // call changes the hook count between the list render and a child-route
  // render of this same layout-route instance, which trips React error #300
  // ("Rendered fewer hooks than expected").
  const isOnChildRoute = location.pathname !== `/services/${serviceId}/assessments`;
  if (isOnChildRoute) return <Outlet />;

  const assessments = data?.maturityAssessments?.edges?.map((e) => e.node) ?? [];

  const frameworksById = new Map(
    (frameworksData?.maturityFrameworks?.edges ?? []).map((e) => [e.node.entityId, e.node])
  );
  const columns = buildColumns(frameworksById);

  const cadence: AssessmentCadence =
    (serviceData?.service?.assessmentCadence as AssessmentCadence | null | undefined) ??
    AssessmentCadence.SemiAnnual;

  const scheduleInfo = computeAssessmentScheduleInfo(assessments, cadence);

  const handleRowClick = (assessment: MaturityAssessment): void => {
    void navigate({
      to: '/services/$serviceId/assessments/$assessmentId',
      params: { serviceId, assessmentId: assessment.entityId },
    });
  };

  const handleCreate = (): void => {
    void navigate({ to: '/services/$serviceId/assessments/new', params: { serviceId } });
  };

  if (loading) {
    return (
      <div className="service-assessments-page">
        <TableSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="service-assessments-page">
        <EmptyState icon="error" title="Failed to load assessments" description={error.message} />
      </div>
    );
  }

  return (
    <div className="service-assessments-page">
      <div className="page-header page-header--toolbar">
        <div className="page-header-left">
          <Link to="/services/$serviceId" params={{ serviceId }} className="back-link">
            ← Back to Service
          </Link>
          <h1 className="forge-typography--headline5">Maturity Assessments</h1>
          <p className="forge-typography--body2">
            {assessments.length} assessment{assessments.length !== 1 ? 's' : ''}
          </p>
        </div>

        <ForgeButton
          type="button"
          variant="outlined"
          onClick={() =>
            void navigate({
              to: '/services/$serviceId/assessments/trends',
              params: { serviceId },
            })
          }
        >
          View Trends
        </ForgeButton>
        {canCreateAssessment(currentUser, serviceData?.service) && (
          <ForgeButton type="button" variant="raised" onClick={handleCreate}>
            New Assessment
          </ForgeButton>
        )}
      </div>

      {/* Assessment Schedule Info — last assessed + next due */}
      <div
        className={`assessment-schedule-banner${scheduleInfo.isOverdue ? ' assessment-schedule-banner--overdue' : ''}`}
        data-testid="assessment-schedule-banner"
        aria-label="Assessment schedule information"
      >
        <div className="assessment-schedule-stat" data-testid="last-assessed-stat">
          <span className="assessment-schedule-label">Last assessed</span>
          <span className="assessment-schedule-value">
            {scheduleInfo.lastAssessedAt !== null
              ? `${scheduleInfo.daysSinceLastAssessment} day${scheduleInfo.daysSinceLastAssessment !== 1 ? 's' : ''} ago`
              : 'Never'}
          </span>
        </div>
        <div className="assessment-schedule-divider" aria-hidden="true" />
        <div className="assessment-schedule-stat" data-testid="next-due-stat">
          <span className="assessment-schedule-label">Next due</span>
          <span className="assessment-schedule-value">
            {scheduleInfo.nextDuePeriod}
            {scheduleInfo.isOverdue && (
              <span className="assessment-schedule-overdue-label" role="status" aria-live="polite">
                {' '}
                — overdue
              </span>
            )}
          </span>
        </div>
        <div className="assessment-schedule-divider" aria-hidden="true" />
        <div className="assessment-schedule-stat" data-testid="cadence-stat">
          <span className="assessment-schedule-label">Cadence</span>
          <span className="assessment-schedule-value">{cadenceLabel(cadence)}</span>
        </div>
      </div>

      {assessments.length === 0 ? (
        <EmptyState
          icon="assignment"
          title="No assessments yet"
          description="Create a maturity assessment to track this service's cloud readiness."
        />
      ) : (
        <DataTable
          columns={columns}
          data={assessments}
          getRowKey={(a) => a.entityId}
          onRowClick={handleRowClick}
        />
      )}
    </div>
  );
}
